import type { InstrumentMetadata } from './instruments';

export interface ReadingEvidence {
  mainScale?: boolean;
  vernierCoincidence?: boolean;
  thimbleScale?: boolean;
  eyeLevel?: boolean;
  bottomMeniscus?: boolean;
  zeroCorrection?: boolean;
  parallaxCorrection?: boolean;
  units?: boolean;
  uncertainty?: boolean;
  significantFigures?: boolean;
}

export interface ReadingScore {
  score: number;
  difference: number;
  tolerance: number;
  accurate: boolean;
  evidenceComplete: boolean;
  feedback: string;
}

export function requiredReadingEvidence(instrument: InstrumentMetadata, zeroError = 0, parallaxPresent = false): Array<keyof ReadingEvidence> {
  const steps: Array<keyof ReadingEvidence> = [];
  if (instrument.scale === 'vernier') steps.push('mainScale', 'vernierCoincidence');
  else if (instrument.id === 'micrometer') steps.push('mainScale', 'thimbleScale');
  else if (instrument.scale === 'meniscus') steps.push('eyeLevel', 'bottomMeniscus');
  else steps.push('mainScale');
  if (zeroError !== 0) steps.push('zeroCorrection');
  if (parallaxPresent) steps.push('parallaxCorrection');
  steps.push('units', 'uncertainty', 'significantFigures');
  return steps;
}

/** Numeric accuracy alone is never sufficient for full credit: the method evidence must also be complete. */
export function scoreInstrumentReading(
  submitted: number,
  expected: number,
  tolerance: number,
  instrument: InstrumentMetadata,
  evidence: ReadingEvidence,
  zeroError = 0,
  parallaxPresent = false,
): ReadingScore {
  if (![submitted, expected, tolerance].every(Number.isFinite) || tolerance <= 0) {
    throw new RangeError('Submitted reading, expected value, and positive tolerance are required.');
  }
  const difference = Math.abs(submitted - expected);
  const accurate = difference <= tolerance;
  const required = requiredReadingEvidence(instrument, zeroError, parallaxPresent);
  const evidenceComplete = required.every((step) => evidence[step] === true);
  let score = accurate ? 70 : difference <= tolerance * 2 ? 40 : difference <= tolerance * 4 ? 20 : 0;
  if (accurate && evidenceComplete) score = 100;
  const feedback = score === 100
    ? 'Accurate reading with the required measurement method recorded.'
    : accurate
      ? 'The value is accurate, but method evidence is incomplete; a numerical match alone does not receive full credit.'
      : score > 0
        ? 'Close reading. Recheck the scale alignment, zero correction, meniscus, or units.'
        : 'Reading is outside the accepted tolerance. Inspect the instrument scale and try again.';
  return { score, difference, tolerance, accurate, evidenceComplete, feedback };
}
